// valley lab: two tanks on two hills, pipe down into the valley and up again; gravity (fall) or pumped main, direction L→R or R→L
import { leakAreaM2 } from './leakShape';

const G = 9.81, BAR = 10.197;
const hf = (Q, len, D, k) => { if (Q <= 0 || len <= 0) return 0; const A = (Math.PI * D * D) / 4, v = Q / A, Re = (v * D) / 1.3e-6, f = 0.25 / Math.log10(k / 1000 / (3.7 * D) + 5.74 / Re ** 0.9) ** 2; return (f * len * v * v) / (D * 2 * G); };

// pipe elevation along x (m from the left tank): left hill -> valley -> right hill
export const zAt = (o, x) => { if (o.prof) { const P = o.prof; if (x <= P[0][0]) return P[0][1]; for (let i = 1; i < P.length; i += 1) if (x <= P[i][0]) { const [a, b] = [P[i - 1], P[i]]; return a[1] + ((b[1] - a[1]) * (x - a[0])) / ((b[0] - a[0]) || 1); } return P[P.length - 1][1]; } const xv = (o.xv / 100) * o.L; return x <= xv ? o.zL + ((o.zV - o.zL) * x) / (xv || 1) : o.zV + ((o.zR - o.zV) * (x - xv)) / ((o.L - xv) || 1); };

// walk from the source with total flow Q0: returns end head, end flow, the HGL and the draws (leak, hydrant left H1, hydrant right H2)
function walk(o, Q0, st) {
  const src = o.dir === 'LR' ? 'L' : 'R', X = (s) => (src === 'L' ? s : o.L - s), S = (x) => (src === 'L' ? x : o.L - x);
  const D = o.d / 1000, a = leakAreaM2(o, o.d), qf = o.shape === 'qty' ? o.lq / 1000 : null;
  const Hs = (src === 'L' ? o.zL + o.lvL : o.zR + o.lvR) + (o.mode === 'pump' ? Math.max(0, o.h0 - (o.h0 - o.h1) * ((Q0 * 1000) / o.q1) ** 2) : 0);
  const ev = [...(st.leak ? [{ s: S(o.xLeak), k: 'leak' }] : []), ...(st.q1 > 0 ? [{ s: S(o.xHyd), k: 'h', q: st.q1 }] : []), ...(st.q2 > 0 ? [{ s: S(o.xHyd2), k: 'h', q: st.q2 }] : [])].sort((p, q) => p.s - q.s);
  let H = Hs, Q = Q0, s = 0, QL = 0, Qh = 0;
  const hgl = [[X(0), H]];
  for (const e of [...ev, { s: o.L, k: 'end' }]) {
    H -= hf(Q, e.s - s, D, o.k); s = e.s; hgl.push([X(s), H]);
    const p = H - zAt(o, X(s));
    if (e.k === 'leak') { const q = p > 0 ? (qf != null ? qf : 0.6 * a * Math.sqrt(2 * G * p)) : 0; QL = Math.min(q, Math.max(0, Q)); Q -= QL; }
    if (e.k === 'h') { const q = Math.min(p > 0 ? e.q / 1000 : 0, Math.max(0, Q)); Qh += q; Q -= q; }
  }
  return { H, Qe: Q, QL, Qh, hgl, Hs };
}
const interp = (pts, x) => { const P = [...pts].sort((a, b) => a[0] - b[0]); for (let i = 1; i < P.length; i += 1) if (x <= P[i][0]) { const [a, b] = [P[i - 1], P[i]]; return a[1] + ((b[1] - a[1]) * (x - a[0])) / ((b[0] - a[0]) || 1); } return P[P.length - 1][1]; };

// state: { leak: bool, q1 / q2: l/s draw-off at hydrant left / right, closed: receiver inlet closed } -> b1/b2/b3 = bar at MP 1 (H1), MP 2 (H2), MP 3
export function solveValley(o, st) {
  const top = o.dir === 'LR' ? o.zR + o.maxR : o.zL + o.maxL;
  const fit = (fn, lo, hi) => { for (let i = 0; i < 60; i += 1) { const m = (lo + hi) / 2; if (fn(m) > 0) lo = m; else hi = m; } return (lo + hi) / 2; };
  // closed inlet (or no flow possible): only the draws flow -> remaining end flow 0
  const dead = () => walk(o, fit((q) => walk(o, q, st).Qe > 1e-7 ? -1 : 1, 0, 0.5), st);
  let r;
  if (st.closed) r = dead();
  else {
    const q0 = fit((q) => walk(o, q, st).H - top, 0, 0.5), w = walk(o, q0, st);
    r = w.Qe > 0 && Math.abs(w.H - top) < 0.05 ? w : dead();
  }
  const bar = (x) => (interp(r.hgl, x) - zAt(o, x)) / BAR;
  return { ...r, Qe: Math.max(0, r.Qe), top, b1: bar(o.xHyd), b2: bar(o.xHyd2), b3: bar(o.xM2) };
}

// lowest pipe point (valley floor)
export const lowX = (o) => (o.prof ? o.prof.reduce((m, p) => (p[1] < m[1] ? p : m))[0] : (o.xv / 100) * o.L);

// ③ locate: receiver + hydrants closed, measured static pressures m = [MP 1, MP 2, MP 3] (null = not measured) -> leak positions that explain them
export function locateValley(o, m) {
  const hits = [];
  for (let x = 0; x <= o.L; x += o.L / 200) { const r = solveValley({ ...o, xLeak: x }, { leak: true, closed: true }), b = [r.b1, r.b2, r.b3]; hits.push([x, m.reduce((t, v, i) => t + (v != null ? Math.abs(b[i] - v) : 0), 0)]); }
  const best = Math.min(...hits.map((h) => h[1]));
  return hits.filter((h) => h[1] <= Math.max(0.01, best + 0.005)).map((h) => h[0]);
}
